import { partnerCategoryLabel, partnerDisplayName } from "@roof-hawk/core";
import { useMemo } from "react";

import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchPartners,
  fetchReferrals,
  fetchTemplates,
  type Partner,
} from "../../lib/partnerQueries";
import { supabase, unwrap } from "../../lib/supabase";

/**
 * Is any of this working?
 *
 * Four questions, and one honest caveat printed on the screen rather than
 * buried: **replies are undercounted.** They land on the rep's own phone, which
 * Roof Board has no access to, so a reply only appears here if somebody
 * remembered to log it. A screen that showed "3 replies" as though it were a
 * measurement would be worse than one that says what it is.
 */

interface TemplateStat {
  template_id: string | null;
  sent: number;
}

/** Sends per template, counted in the database rather than in the browser. */
async function fetchTemplateSends(): Promise<TemplateStat[]> {
  const rows = unwrap<{ template_id: string | null }[]>(
    await supabase
      .from("partner_messages")
      .select("template_id")
      .eq("status", "sent")
      .eq("direction", "outbound"),
  );
  const counts = new Map<string | null, number>();
  for (const row of rows) {
    counts.set(row.template_id, (counts.get(row.template_id) ?? 0) + 1);
  }
  return [...counts].map(([template_id, sent]) => ({ template_id, sent }));
}

async function fetchLoggedReplies(): Promise<number> {
  const rows = unwrap<{ id: string }[]>(
    await supabase
      .from("partner_messages")
      .select("id")
      .eq("direction", "inbound"),
  );
  return rows.length;
}

const daysSince = (iso: string | null): number =>
  iso ? Math.floor((Date.now() - Date.parse(iso)) / 86_400_000) : Infinity;

export function PartnerReports() {
  const partnersQuery = useAsync(fetchPartners, []);
  const referralsQuery = useAsync(() => fetchReferrals(), []);
  const templatesQuery = useAsync(fetchTemplates, []);
  const sendsQuery = useAsync(fetchTemplateSends, []);
  const repliesQuery = useAsync(fetchLoggedReplies, []);

  // Held steady across renders: `?? []` makes a new array each time, and both
  // memos below take these as dependencies — so without this they recompute
  // the whole scoreboard on every keystroke anywhere on the page.
  const partners = useMemo(
    () => partnersQuery.data ?? [],
    [partnersQuery.data],
  );
  const referrals = useMemo(
    () => referralsQuery.data ?? [],
    [referralsQuery.data],
  );

  const scoreboard = useMemo(() => {
    const rows = new Map<
      string,
      { partner: Partner; from: number; to: number; won: number }
    >();
    for (const partner of partners) {
      rows.set(partner.id, { partner, from: 0, to: 0, won: 0 });
    }
    for (const referral of referrals) {
      const row = rows.get(referral.partner_id);
      if (!row) continue;
      if (referral.direction === "from_partner") row.from += 1;
      else row.to += 1;
      if (referral.outcome === "won") row.won += 1;
    }
    return [...rows.values()].sort(
      (a, b) => b.from + b.won - (a.from + a.won) || b.to - a.to,
    );
  }, [partners, referrals]);

  /** Nobody has heard from us, and they have sent us nothing, in 90 days. */
  const goneQuiet = useMemo(() => {
    const lastReferral = new Map<string, string>();
    for (const referral of referrals) {
      const current = lastReferral.get(referral.partner_id);
      if (!current || referral.created_at > current) {
        lastReferral.set(referral.partner_id, referral.created_at);
      }
    }
    return partners.filter(
      (partner) =>
        daysSince(partner.last_texted_at) > 90 &&
        daysSince(lastReferral.get(partner.id) ?? null) > 90,
    );
  }, [partners, referrals]);

  const sentByTemplate = new Map(
    (sendsQuery.data ?? []).map((row) => [row.template_id, row.sent]),
  );

  return (
    <>
      <div className="listhead">
        <h2>Partner reports</h2>
      </div>

      <div className="scoreboard">
        <div className="sb-fig">
          <span className="k">Partners</span>
          <b>{partners.length}</b>
        </div>
        <div className="sb-fig">
          <span className="k">Referrals in</span>
          <b>
            {referrals.filter((row) => row.direction === "from_partner").length}
          </b>
        </div>
        <div className="sb-fig">
          <span className="k">Sold from a referral</span>
          <b className="good">
            {referrals.filter((row) => row.outcome === "won").length}
          </b>
        </div>
        <div className={`sb-fig flag${goneQuiet.length === 0 ? " quiet" : ""}`}>
          <span className="k">Quiet 90 days</span>
          <b>{goneQuiet.length}</b>
        </div>
      </div>

      <section>
        <div className="listhead">
          <h2>Both directions</h2>
          <span className="count">
            The number that matters is the ratio, not either column
          </span>
        </div>
        <div className="list">
          <div className="lrow head">
            <div>Partner</div>
            <div>Category</div>
            <div>They sent us</div>
            <div>We sent them</div>
            <div>Sold</div>
          </div>
          {scoreboard.slice(0, 40).map((row) => (
            <div
              key={row.partner.id}
              className="lrow"
              onClick={() => navigate(`partner=${row.partner.id}`)}
            >
              <div className="cust">{partnerDisplayName(row.partner)}</div>
              <div className="sub">
                {partnerCategoryLabel(row.partner.category)}
              </div>
              <div className="lc-val">{row.from}</div>
              <div className="lc-val">{row.to}</div>
              <div className="lc-val">{row.won}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="listhead">
          <h2>Nothing in 90 days</h2>
          <span className="count">
            No text out, nothing in. The list to work through.
          </span>
        </div>
        <div className="list">
          {goneQuiet.length === 0 ? (
            <div className="empty">
              <b>Nobody</b>
              Everybody has heard from somebody this quarter.
            </div>
          ) : (
            goneQuiet.map((partner) => (
              <div
                key={partner.id}
                className="lrow"
                onClick={() => navigate(`partner=${partner.id}`)}
              >
                <div className="cust">{partnerDisplayName(partner)}</div>
                <div className="sub">{partner.company ?? "—"}</div>
                <div className="sub">
                  {partnerCategoryLabel(partner.category)}
                </div>
                <div className="sub">
                  {partner.owner?.full_name ?? "Unowned"}
                </div>
                <div className="sub">
                  {partner.sms_status === "opted_in" ? "" : partner.sms_status}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section>
        <div className="listhead">
          <h2>Which templates get used</h2>
        </div>
        <div className="caveat">
          <b>Replies are undercounted, on purpose</b>
          <p>
            {repliesQuery.data ?? 0} logged in total. They arrive on the
            rep&apos;s own phone, which Roof Board cannot see, so a reply only
            appears here if somebody typed it in. Read the send column as a fact
            and the reply count as a floor.
          </p>
        </div>
        <div className="list">
          <div className="lrow head">
            <div>Template</div>
            <div>Who gets it</div>
            <div>Sent</div>
          </div>
          {(templatesQuery.data ?? []).map((template) => (
            <div key={template.id} className="lrow">
              <div>
                <div className="cust">{template.name}</div>
                <div className="sub">{template.body.slice(0, 90)}…</div>
              </div>
              <div className="sub">
                {template.category
                  ? partnerCategoryLabel(template.category)
                  : "Everybody"}
              </div>
              <div className="lc-val">
                {sentByTemplate.get(template.id) ?? 0}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
